/**
 * The card detail screen's spending-limit panel.
 *
 * Lives beside `cardSpending.ts` rather than inside `configs/cards.tsx` because a config module
 * exports a config, not components: mixing the two is what
 * `react-refresh/only-export-components` flags, and keeping the components here is what the
 * rule's own message asks for. It also lets the tests render the panel without pulling the
 * whole entity registry in.
 */
import { useMemo } from "react";
import { Alert, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { Money } from "@/components/Money";
import { FieldRow } from "@/components/DetailGrid";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreQuery } from "@/firestore/hooks";
import type { CardDoc, CardSpendingPolicy, PaymentDoc, WithDocId } from "@/firestore/types";
import {
  capturedPaymentsQuery,
  legacyMirrorRole,
  policyLimitMicroUsdc,
  spendingWindowLabel,
  spendingWindowStartMs,
  spendUnavailable,
  summarizeWindowSpend,
} from "./cardSpending";
import type { WindowSpend } from "./cardSpending";

export interface CardSpendingPolicyViewProps {
  policy: CardSpendingPolicy | null | undefined;
  /** Spend inside the window; null while loading, on error, or when it can't be trusted. */
  spend: WindowSpend | null;
  loading: boolean;
  error: Error | null;
}

/**
 * The card's limit and what has been spent against it inside the rolling window.
 *
 * Read-only by design, and not an oversight: `PUT /cards/{id}/spendingPolicy` is `assertOwnsCard`,
 * so an admin token 403s there. The limit is a value decision the cardholder authorises; an
 * operator acts on the card's lifecycle instead (freeze / unfreeze / terminate, in the header).
 *
 * Deliberately shows no reset date. A rolling window has none — see {@link CardSpendingPolicy}.
 */
export function CardSpendingPolicyView({
  policy,
  spend,
  loading,
  error,
}: CardSpendingPolicyViewProps) {
  const limit = policyLimitMicroUsdc(policy);
  const unavailable = spendUnavailable({ window: policy?.window, loading, error, spend });
  const spent = !unavailable && spend ? spend.totalMicroUsdc : null;
  const remaining = limit !== null && spent !== null ? limit - spent : null;
  const mirrorRole = legacyMirrorRole(policy);

  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <FieldRow label="Limit" value={<Money micro={limit} fw={600} />} />
        <FieldRow label="Window" value={spendingWindowLabel(policy?.window)} />
        <FieldRow
          label="Captured in window"
          value={
            spent === null || !spend ? (
              <Text size="sm" c="dimmed">
                {unavailable}
              </Text>
            ) : (
              <Group gap="xs" wrap="nowrap">
                <Money micro={spent} />
                <Text size="xs" c="dimmed">
                  {spend.count === 1 ? "1 capture" : `${spend.count} captures`}
                </Text>
              </Group>
            )
          }
        />
        <FieldRow
          label="Remaining"
          value={
            remaining === null ? (
              <Text size="sm" c="dimmed">
                —
              </Text>
            ) : (
              <Group gap="xs" wrap="nowrap">
                <Money micro={remaining} c={remaining < 0 ? "red" : undefined} />
                {remaining < 0 ? (
                  <Text size="xs" c="red">
                    over the limit
                  </Text>
                ) : null}
              </Group>
            )
          }
        />
      </SimpleGrid>

      <Alert color="gray" variant="light" title="Enforced by Rain over a rolling 30-day window">
        <Text size="sm">
          Rain declines an authorization that would breach this limit; Elios does not re-check it.
          The window is the trailing 30 days from this moment, so there is no period, no reset date
          and nothing to renew — each capture simply rolls off 30 days after it lands.
        </Text>
        <Text size="sm" mt="xs">
          The figures above are computed here from this card&apos;s captured payments. Rain keeps
          its own counter and that is the one a decline is made against, so treat these as a close
          estimate rather than the authority — an approved-but-uncaptured authorization is not in
          them.
        </Text>
        <Text size="sm" mt="xs">
          Changing the limit is owner-authorised (the endpoint is owner-gated, so an admin token is
          refused). The cardholder changes it in the app; operators act on the card&apos;s lifecycle
          instead.
        </Text>
      </Alert>

      {mirrorRole === "source" ? (
        <Text size="xs" c="dimmed">
          The Limit above was read from <code>monthlyLimitMicroUsdc</code> in the raw document
          below: this document was last projected before the rolling-window change, so it carries no{" "}
          <code>limitMicroUsdc</code> yet. The backend writes that field as an exact copy of the
          limit, and will add the new one the next time this card is projected. Any{" "}
          <code>currentPeriodStart</code> beside it is the card&apos;s creation time standing in for
          a period start that no longer exists — it is not a reset date.
        </Text>
      ) : null}
      {mirrorRole === "mirror" ? (
        <Text size="xs" c="dimmed">
          The raw document below also carries <code>monthlyLimitMicroUsdc</code> and{" "}
          <code>currentPeriodStart</code>. Both are deprecated compatibility mirrors for app builds
          shipped before this change: the first is a copy of the limit, the second is the
          card&apos;s creation time standing in for a period start that no longer exists. Neither is
          enforced, and neither is a reset date.
        </Text>
      ) : null}
    </Stack>
  );
}

/**
 * Loads the captures the window is summed from, and hands them to the view.
 *
 * The related "Payments" list further down the screen is not a substitute: it pages at 50 rows,
 * orders by `approvedAt` and includes every state.
 */
export function CardSpendingPolicyBlock({ card }: { card: WithDocId<CardDoc> }) {
  // Frozen at mount: a window start recomputed on every render would re-key the subscription
  // (the read hook keys on the serialized query spec) and resubscribe forever. A 30-day window
  // does not care that `now` is a few minutes stale.
  const windowStartMs = useMemo(() => spendingWindowStartMs(Date.now()), []);
  const spec = useMemo(
    () => capturedPaymentsQuery(card.owner, new Date(windowStartMs)),
    [card.owner, windowStartMs],
  );
  // No window on the document means no basis for a figure, so there is nothing to subscribe to.
  const enabled = card.spendingPolicy?.window === "per30Days";
  const { data, loading, error } = useFirestoreQuery<PaymentDoc>(
    COLLECTIONS.payments,
    spec,
    enabled,
  );
  const spend =
    loading || error || !enabled
      ? null
      : summarizeWindowSpend(data, { cardId: card.id, windowStartMs });

  return (
    <CardSpendingPolicyView
      policy={card.spendingPolicy}
      spend={spend}
      loading={loading}
      error={error}
    />
  );
}
